import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "./api.ts";

export type MessageTemplate = components["schemas"]["MessageTemplateDetails"];
export type DeliveryLogEntry = components["schemas"]["DeliveryLogEntry"];
export type OutboxMessageStatus = components["schemas"]["OutboxMessageStatus"];

export const notificationKeys = {
  templates: ["notifications", "templates"] as const,
  log: (filters: object) => ["notifications", "log", filters] as const,
};

/** The server's template syntax (Features/TemplateText.cs): {name}, starting with a letter. */
const PLACEHOLDER = /\{([A-Za-z][A-Za-z0-9_]*)\}/g;

/** Client-side mirror of the server's placeholder check, so most mistakes show before saving. The server stays the authority. */
export function templateTextProblems(text: string, allowed: readonly string[]): { unknown: string[]; strayBrace: boolean } {
  const used = [...text.matchAll(PLACEHOLDER)].map((m) => m[1] ?? "");
  const unknown = [...new Set(used.filter((name) => !allowed.includes(name)))].map((name) => `{${name}}`);
  const rest = text.replace(PLACEHOLDER, "");
  return { unknown, strayBrace: rest.includes("{") || rest.includes("}") };
}

export function useMessageTemplates() {
  const { t } = useTranslation();
  return useQuery({
    queryKey: notificationKeys.templates,
    queryFn: async () => {
      const { data, error } = await api.GET("/notifications/templates");
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });
}
